import _taggedTemplateLiteral from '@babel/runtime/helpers/esm/taggedTemplateLiteral';
import { css, useTheme, jsx } from '@emotion/react';
import { math, stripUnit, remToPx } from 'polished';
import { devWarning, utilStyles, ZINDEX_DROPDOWN, uniqueId, hasWindow, hasDocument, highlightMatch, KeyboardCode } from '@paypalcorp/pp-react-utils';
import { textStyles, CaptionText, BodyText } from '@paypalcorp/pp-react-text';
import _toConsumableArray from '@babel/runtime/helpers/esm/toConsumableArray';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _extends from '@babel/runtime/helpers/esm/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import React, { useRef, useState, useMemo, useLayoutEffect, useEffect } from 'react';
import { CheckmarkIcon } from '@paypalcorp/pp-react-icons';
import { LoadingSpinner } from '@paypalcorp/pp-react-loading-spinner';
import { Sheet } from '@paypalcorp/pp-react-modal';
import { List } from '@paypalcorp/pp-react-list';

var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6, _templateObject7, _templateObject8;
function menuListStyles(tokens) {
  /* istanbul ignore next */
  if (!Object.keys(tokens).length) {
    devWarning('Missing Emotion theme. Use ThemeProvider from @emotion/react to pass a valid theme from pp-react.');
    return {};
  }

  var sysColorBackgroundSurface = tokens.sysColorBackgroundSurface,
      sysColorBackgroundSurfaceHover = tokens.sysColorBackgroundSurfaceHover,
      sysColorBackgroundSurfaceActive = tokens.sysColorBackgroundSurfaceActive,
      sysColorBackgroundMediumContrast = tokens.sysColorBackgroundMediumContrast,
      sysBorderRadiusLg = tokens.sysBorderRadiusLg,
      sysSpaceXs = tokens.sysSpaceXs,
      sysSpaceSm = tokens.sysSpaceSm,
      sysSpaceMd = tokens.sysSpaceMd,
      sysSpaceLg = tokens.sysSpaceLg,
      sysBorderThicknessNormal = tokens.sysBorderThicknessNormal,
      sysSizeMediaMd = tokens.sysSizeMediaMd;

  var _utilStyles = utilStyles(tokens),
      elevationStyles = _utilStyles.elevationStyles,
      focusStyles = _utilStyles.focusStyles;

  var _textStyles = textStyles(tokens),
      textBody = _textStyles.body,
      textCaption = _textStyles.caption; // component specific tokens


  var menuListWidthMin = '8rem';

  // 34px
  function menuContainer() {
    var args = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var _args$borderRadius = args.borderRadius,
        borderRadius = _args$borderRadius === void 0 ? sysBorderRadiusLg : _args$borderRadius,
        _args$backgroundColor = args.backgroundColor,
        backgroundColor = _args$backgroundColor === void 0 ? sysColorBackgroundSurface : _args$backgroundColor,
        _args$marginTop = args.marginTop,
        marginTop = _args$marginTop === void 0 ? sysSpaceXs : _args$marginTop,
        _args$maxHeight = args.maxHeight,
        maxHeight = _args$maxHeight === void 0 ? '30rem' : _args$maxHeight,
        _args$minWidth = args.minWidth,
        minWidth = _args$minWidth === void 0 ? menuListWidthMin : _args$minWidth,
        _args$outline = args.outline,
        outline = _args$outline === void 0 ? sysBorderThicknessNormal : _args$outline;
    return css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n      label: menu_container;\n      border-radius: ", ";\n      min-width: ", ";\n      max-height: ", ";\n      overflow-y: auto;\n      overflow-x: hidden;\n      position: absolute;\n      margin-top: ", ";\n      ", ";\n      background-color: ", ";\n      outline: ", " solid transparent; // for HCM View\n      z-index: ", ";\n    "])), borderRadius, minWidth, maxHeight, marginTop, elevationStyles.level3, backgroundColor, outline, ZINDEX_DROPDOWN);
  }

  function menuList() {
    var args = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var _args$desktopPaddingB = args.desktopPaddingBottom,
        desktopPaddingBottom = _args$desktopPaddingB === void 0 ? sysSpaceSm : _args$desktopPaddingB,
        _args$desktopPaddingT = args.desktopPaddingTop,
        desktopPaddingTop = _args$desktopPaddingT === void 0 ? sysSpaceSm : _args$desktopPaddingT;
    var _args$paddingBottom = args.paddingBottom,
        paddingBottom = _args$paddingBottom === void 0 ? sysSpaceSm : _args$paddingBottom,
        _args$paddingTop = args.paddingTop,
        paddingTop = _args$paddingTop === void 0 ? sysSpaceSm : _args$paddingTop; // overwrite the padding if it is isMobile

    if (args.isMobile) {
      paddingBottom = '2.125rem';
      paddingTop = paddingTop;
    }

    return css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n      label: list;\n      list-style: none;\n      margin: 0;\n      padding: ", " 0 ", " 0;\n      background-color: transparent;\n      @media (min-width: ", ") {\n        padding-bottom: ", ";\n        padding-top: ", ";\n      }\n    "])), paddingTop, paddingBottom, sysSizeMediaMd, desktopPaddingBottom, desktopPaddingTop);
  }

  function menuListItem() {
    var args = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var _args$paddingRight = args.paddingRight,
        paddingRight = _args$paddingRight === void 0 ? sysSpaceLg : _args$paddingRight,
        _args$paddingLeft = args.paddingLeft,
        paddingLeft = _args$paddingLeft === void 0 ? sysSpaceLg : _args$paddingLeft,
        _args$desktopPaddingB2 = args.desktopPaddingBottom,
        desktopPaddingBottom = _args$desktopPaddingB2 === void 0 ? sysSpaceSm : _args$desktopPaddingB2,
        _args$desktopPaddingT2 = args.desktopPaddingTop,
        desktopPaddingTop = _args$desktopPaddingT2 === void 0 ? sysSpaceSm : _args$desktopPaddingT2,
        _args$hoverBgColor = args.hoverBgColor,
        hoverBgColor = _args$hoverBgColor === void 0 ? sysColorBackgroundSurfaceHover : _args$hoverBgColor,
        _args$activeBgColor = args.activeBgColor,
        activeBgColor = _args$activeBgColor === void 0 ? sysColorBackgroundSurfaceActive : _args$activeBgColor;
    var _args$paddingTop2 = args.paddingTop,
        paddingTop = _args$paddingTop2 === void 0 ? desktopPaddingTop : _args$paddingTop2,
        _args$paddingBottom2 = args.paddingBottom,
        paddingBottom = _args$paddingBottom2 === void 0 ? desktopPaddingBottom : _args$paddingBottom2; // overwrite the padding if it is mobile

    if (args.isMobile) {
      paddingBottom = sysSpaceSm;
      paddingTop = sysSpaceSm;
    }

    return css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n      label: list_item;\n      padding: ", " ", " ", " ", ";\n      outline: none;\n      position: relative;\n      display: flex;\n      align-items: center;\n      cursor: pointer;\n\n      @media (min-width: ", ") {\n        padding-bottom: ", ";\n        padding-top: ", ";\n      }\n\n      &:hover {\n        background-color: ", ";\n      }\n\n      &:active {\n        background-color: ", ";\n      }\n    "])), paddingTop, paddingRight, paddingBottom, paddingLeft, sysSizeMediaMd, desktopPaddingBottom, desktopPaddingTop, hoverBgColor, activeBgColor);
  }

  function menuItemThumbnail() {
    var args = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var _args$marginRightLeft = args.marginRightLeft,
        marginRightLeft = _args$marginRightLeft === void 0 ? sysSpaceMd : _args$marginRightLeft;
    return css(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n      label: thumbnail;\n      display: flex;\n      align-items: center;\n      margin-right: ", ";\n      min-height: auto;\n      [dir='rtl'] & {\n        margin-right: 0;\n        margin-left: ", ";\n      }\n      pointer-events: none;\n    "])), marginRightLeft, marginRightLeft);
  }

  function menuItemText() {
    return css(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n      label: item_text;\n      margin: 0; /* overwrite default margin */\n      pointer-events: none;\n      ", ";\n    "])), textBody());
  }

  function menuSecondaryText() {
    var args = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var _args$paddingTop3 = args.paddingTop,
        paddingTop = _args$paddingTop3 === void 0 ? sysSpaceXs : _args$paddingTop3;
    return css(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n      label: secondary_text;\n      display: -webkit-box;\n      padding-top: ", ";\n      pointer-events: none;\n      ", ";\n    "])), paddingTop, textCaption({
      color: sysColorBackgroundMediumContrast
    }));
  }

  function menuLoadingContainer() {
    return css(_templateObject7 || (_templateObject7 = _taggedTemplateLiteral(["\n      label: loading_container;\n      display: flex;\n      height: 100%;\n      align-items: center;\n      justify-content: center;\n    "])));
  }

  var menuKeyboardActive = css(_templateObject8 || (_templateObject8 = _taggedTemplateLiteral(["\n    label: keyboard_active;\n    &:focus {\n      ", "\n    }\n  "])), focusStyles.focusInlineElement);
  return {
    menuContainer: menuContainer,
    menuList: menuList,
    menuListItem: menuListItem,
    menuItemThumbnail: menuItemThumbnail,
    menuItemText: menuItemText,
    menuSecondaryText: menuSecondaryText,
    menuLoadingContainer: menuLoadingContainer,
    menuKeyboardActive: menuKeyboardActive,
    menuListWidthMin: menuListWidthMin,
    menuListWidthMax: '67.5rem'
  };
}

var _templateObject$1, _templateObject2$1, _templateObject3$1, _templateObject4$1, _templateObject5$1, _templateObject6$1, _templateObject7$1;
function styles(tokens) {
  /* istanbul ignore next */
  if (!Object.keys(tokens).length) {
    devWarning('Missing Emotion theme. Use ThemeProvider from @emotion/react to pass a valid theme from pp-react.');
    return {};
  }

  var sysSpaceSm = tokens.sysSpaceSm,
      sysSpaceMd = tokens.sysSpaceMd,
      sysSpaceLg = tokens.sysSpaceLg,
      sysSizeCircleSm = tokens.sysSizeCircleSm,
      sysSizeIconSm = tokens.sysSizeIconSm,
      sysColorBackgroundSurfaceHover = tokens.sysColorBackgroundSurfaceHover;

  var _menuListStyles = menuListStyles(tokens),
      menuContainer = _menuListStyles.menuContainer,
      menuList = _menuListStyles.menuList,
      menuListItem = _menuListStyles.menuListItem,
      menuItemThumbnail = _menuListStyles.menuItemThumbnail,
      menuItemText = _menuListStyles.menuItemText,
      menuSecondaryText = _menuListStyles.menuSecondaryText,
      menuLoadingContainer = _menuListStyles.menuLoadingContainer,
      menuKeyboardActive = _menuListStyles.menuKeyboardActive;

  var sMenuItemTextSelectedSpacing = math("".concat(sysSizeIconSm, " + ").concat(sysSpaceSm)); // 24px + 8px
  // selection menu

  var selectionMenuItemText = css(_templateObject$1 || (_templateObject$1 = _taggedTemplateLiteral(["\n    ", ";\n    margin-right: ", ";\n\n    [dir='rtl'] & {\n      margin-right: 0;\n      margin-left: ", ";\n    }\n  "])), menuItemText(), sMenuItemTextSelectedSpacing, sMenuItemTextSelectedSpacing);
  var selectedIcon = css(_templateObject2$1 || (_templateObject2$1 = _taggedTemplateLiteral(["\n    label: selected_icon;\n    position: absolute;\n    top: 50%;\n    right: ", ";\n    transform: translateY(-50%);\n\n    [dir='rtl'] & {\n      left: ", ";\n      right: auto;\n    }\n  "])), sysSpaceLg, sysSpaceLg);
  var itemThumbnailImage = css(_templateObject3$1 || (_templateObject3$1 = _taggedTemplateLiteral(["\n    label: thumbnail_image;\n    width: ", ";\n  "])), sysSizeCircleSm);
  var itemHilite = css(_templateObject4$1 || (_templateObject4$1 = _taggedTemplateLiteral(["\n    label: hilite;\n    background-color: ", ";\n  "])), sysColorBackgroundSurfaceHover);
  var unselectable = css(_templateObject5$1 || (_templateObject5$1 = _taggedTemplateLiteral(["\n    label: unselectable;\n    pointer-events: none;\n  "])));
  var content = css(_templateObject6$1 || (_templateObject6$1 = _taggedTemplateLiteral(["\n    label: content;\n    padding: ", " ", ";\n  "])), sysSpaceMd, sysSpaceLg);
  var suggestionSublist = css(_templateObject7$1 || (_templateObject7$1 = _taggedTemplateLiteral(["\n    label: suggestion_sublist;\n    list-style-type: none;\n    margin: 0;\n    padding: 0;\n  "])));
  return {
    menuContainer: menuContainer,
    menuList: menuList,
    menuListItem: menuListItem,
    menuItemText: menuItemText,
    menuSecondaryText: menuSecondaryText,
    menuItemThumbnail: menuItemThumbnail,
    menuLoadingContainer: menuLoadingContainer,
    menuKeyboardActive: menuKeyboardActive,
    selectionMenuItemText: selectionMenuItemText,
    selectedIcon: selectedIcon,
    itemThumbnailImage: itemThumbnailImage,
    itemHilite: itemHilite,
    unselectable: unselectable,
    menuListStyles: menuListStyles,
    suggestionSublist: suggestionSublist,
    content: content
  };
}

var _excluded = ["menuName", "primaryText", "primaryTextLineClamp", "secondaryText", "secondaryTextLineClamp", "decoration", "id", "label", "value", "isFocused", "isHilite", "isSelected", "onItemClick", "className", "unselectable", "isLoadingOptions", "isMobile", "keyboardActive", "highlightText", "isTextHightlighted", "section", "scrollToSelected"],
    _excluded2 = ["name", "value", "className", "containerClassName", "itemClassName", "mobileHeaderClassName", "sheetClassName", "sheetWrapperClassName", "id", "initialItemFocus", "isOpen", "ignoreKeyboard", "mobileTitle", "mobileHeaderContents", "outsideClickCheck", "onOpen", "onClose", "onSelection", "options", "returnFocusToTrigger", "selectedIndex", "activeItem", "triggerElementId", "triggerElementRef", "rtl", "isLoadingOptions", "searchEnabled", "mobileFixedHeight", "hasSections", "highlightText", "isTextHightlighted", "scrollToSelected"];
var ANIMATION_DELAY = 700; //increased ANIMATION_DELAY to 700, so the scroll event can trigger after the sheet-input focus event for the MWeb

/* eslint-disable react/prop-types */

var SelectionMenuOption = function (_ref) {
  var menuName = _ref.menuName,
      primaryText = _ref.primaryText,
      primaryTextLineClamp = _ref.primaryTextLineClamp,
      secondaryText = _ref.secondaryText,
      secondaryTextLineClamp = _ref.secondaryTextLineClamp,
      decoration = _ref.decoration,
      optionId = _ref.id,
      label = _ref.label,
      value = _ref.value,
      isFocused = _ref.isFocused,
      isHilite = _ref.isHilite,
      isSelected = _ref.isSelected,
      onItemClick = _ref.onItemClick,
      className = _ref.className,
      unselectable = _ref.unselectable,
      isLoadingOptions = _ref.isLoadingOptions,
      isMobile = _ref.isMobile,
      keyboardActive = _ref.keyboardActive,
      highlightText = _ref.highlightText,
      isTextHightlighted = _ref.isTextHightlighted,
      section = _ref.section,
      scrollToSelected = _ref.scrollToSelected,
      rest = _objectWithoutProperties(_ref, _excluded);

  var optionRef = useRef(null);
  var itemId = optionId || "".concat(menuName, "_item_").concat(value);
  var tokens = useTheme();

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);

  var itemCssVal = [_styles.menuListItem({
    isMobile: isMobile
  }), keyboardActive && _styles.menuKeyboardActive, unselectable && _styles.unselectable, isHilite && _styles.itemHilite];
  /* istanbul ignore next */

  var scrollIntoSelection = function (option) {
    var _option$parentElement;

    var container = option === null || option === void 0 ? void 0 : (_option$parentElement = option.parentElement) === null || _option$parentElement === void 0 ? void 0 : _option$parentElement.parentElement;

    if (container) {
      var boundingBox = container.getBoundingClientRect();
      var optionBox = option.getBoundingClientRect();

      if (optionBox.bottom > boundingBox.bottom || optionBox.top < boundingBox.top) {
        option.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  };
  /* istanbul ignore next */


  useEffect(function () {
    if (optionRef && optionRef.current) {
      var option = optionRef.current;

      if (isFocused && scrollToSelected) {
        option.focus();
      }

      if ((isFocused || isHilite || isSelected) && scrollToSelected) {
        scrollIntoSelection(option);
      }
    }
  }, [isHilite, isFocused, isSelected, scrollToSelected]);
  /* istanbul ignore next */

  useEffect(function () {
    setTimeout(function () {
      if (optionRef && optionRef.current && isMobile && isSelected) {
        var option = optionRef.current;

        if (isFocused || isSelected) {
          scrollIntoSelection(option);
        }
      }
    }, ANIMATION_DELAY); // Animator component is delayed
  }, []); // This needs to only run first time

  var thumbnail = decoration && jsx("div", {
    css: _styles.menuItemThumbnail,
    "data-ppui": "true"
  }, decoration);
  var optionValue = label ? label : primaryText;
  var optionVal = isTextHightlighted ? highlightMatch(optionValue, highlightText) : optionValue;
  return jsx("li", _extends({
    css: itemCssVal,
    className: className,
    id: itemId,
    role: "option",
    "aria-selected": isSelected,
    tabIndex: -1,
    "data-value": value,
    ref: optionRef,
    onClick: onItemClick
  }, section ? {
    'aria-describedby': section.id
  } : null, rest, {
    "data-ppui": "true"
  }), thumbnail, jsx("p", {
    css: _styles.selectionMenuItemText,
    "data-ppui": "true"
  }, jsx(BodyText, {
    strong: isSelected,
    as: "span",
    clampAtLine: primaryTextLineClamp,
    dangerouslySetInnerHTML: {
      __html: optionVal
    },
    "data-ppui": "true"
  }), secondaryText && jsx(CaptionText, {
    css: _styles.menuSecondaryText,
    as: "span",
    clampAtLine: secondaryTextLineClamp,
    "data-ppui": "true"
  }, secondaryText), isSelected && jsx(CheckmarkIcon, {
    size: "sm",
    css: _styles.selectedIcon,
    color: "SelectedMain",
    "data-ppui": "true"
  })));
};
/*
 * Sigh... We need this if you want to use useState() from a "global" event handler
 * Otherwise you will get the old value of state and it won't ever appear to change.
 * This stores the state (to get refresh) and also stashes it into a ref, which
 * will always be up-to-date
 *
 * Love, BCC
 */


function useReferredState(initialValue) {
  var _React$useState = React.useState(initialValue),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      state = _React$useState2[0],
      setState = _React$useState2[1];

  var reference = React.useRef(state);
  return [reference, function setReferredState(value) {
    reference.current = value;
    setState(value);
  }];
}

var SelectionMenu = function (_ref2) {
  var _styles$menuListStyle;

  var _ref2$name = _ref2.name,
      name = _ref2$name === void 0 ? 'smenu' : _ref2$name,
      value = _ref2.value,
      className = _ref2.className,
      containerClassName = _ref2.containerClassName,
      itemClassName = _ref2.itemClassName,
      mobileHeaderClassName = _ref2.mobileHeaderClassName,
      sheetClassName = _ref2.sheetClassName,
      sheetWrapperClassName = _ref2.sheetWrapperClassName,
      _ref2$id = _ref2.id,
      id = _ref2$id === void 0 ? uniqueId('selectionMenu_') : _ref2$id,
      _ref2$initialItemFocu = _ref2.initialItemFocus,
      initialItemFocus = _ref2$initialItemFocu === void 0 ? true : _ref2$initialItemFocu,
      isOpen = _ref2.isOpen,
      ignoreKeyboard = _ref2.ignoreKeyboard,
      mobileTitle = _ref2.mobileTitle,
      mobileHeaderContents = _ref2.mobileHeaderContents,
      outsideClickCheck = _ref2.outsideClickCheck,
      onOpen = _ref2.onOpen,
      onClose = _ref2.onClose,
      onSelection = _ref2.onSelection,
      options = _ref2.options,
      _ref2$returnFocusToTr = _ref2.returnFocusToTrigger,
      returnFocusToTrigger = _ref2$returnFocusToTr === void 0 ? true : _ref2$returnFocusToTr,
      _ref2$selectedIndex = _ref2.selectedIndex,
      selectedIndex = _ref2$selectedIndex === void 0 ? 0 : _ref2$selectedIndex,
      _ref2$activeItem = _ref2.activeItem,
      activeItem = _ref2$activeItem === void 0 ? -1 : _ref2$activeItem,
      triggerElementId = _ref2.triggerElementId,
      triggerElementRef = _ref2.triggerElementRef,
      _ref2$rtl = _ref2.rtl,
      rtl = _ref2$rtl === void 0 ? false : _ref2$rtl,
      _ref2$isLoadingOption = _ref2.isLoadingOptions,
      isLoadingOptions = _ref2$isLoadingOption === void 0 ? false : _ref2$isLoadingOption,
      _ref2$searchEnabled = _ref2.searchEnabled,
      searchEnabled = _ref2$searchEnabled === void 0 ? false : _ref2$searchEnabled,
      mobileFixedHeight = _ref2.mobileFixedHeight,
      _ref2$hasSections = _ref2.hasSections,
      hasSections = _ref2$hasSections === void 0 ? false : _ref2$hasSections,
      _ref2$highlightText = _ref2.highlightText,
      highlightText = _ref2$highlightText === void 0 ? null : _ref2$highlightText,
      _ref2$isTextHightligh = _ref2.isTextHightlighted,
      isTextHightlighted = _ref2$isTextHightligh === void 0 ? false : _ref2$isTextHightligh,
      _ref2$scrollToSelecte = _ref2.scrollToSelected,
      scrollToSelected = _ref2$scrollToSelecte === void 0 ? true : _ref2$scrollToSelecte,
      rest = _objectWithoutProperties(_ref2, _excluded2);

  var wrapperRef = useRef();
  var menuRef = useRef();
  var loaderRef = useRef();
  var searchText = useRef('');
  var typeToFindTimer;
  var hasOptions = options && options.length > 0;

  var _useReferredState = useReferredState(initialItemFocus ? selectedIndex : -1),
      _useReferredState2 = _slicedToArray(_useReferredState, 2),
      focusedIndexRef = _useReferredState2[0],
      setFocusedIndex = _useReferredState2[1];

  var _useState = useState(true),
      _useState2 = _slicedToArray(_useState, 2),
      keyboardActive = _useState2[0],
      setKeyboardActive = _useState2[1];

  var tokens = useTheme();

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);
  /* istanbul ignore next */


  var _ref3 = ((_styles$menuListStyle = _styles.menuListStyles) === null || _styles$menuListStyle === void 0 ? void 0 : _styles$menuListStyle.call(_styles, tokens)) || {},
      menuListWidthMin = _ref3.menuListWidthMin,
      menuListWidthMax = _ref3.menuListWidthMax;

  var sysSizeMediaMd = tokens.sysSizeMediaMd; // min and max width for ul/li convert into PX

  var MIN_WIDTH = stripUnit(remToPx(menuListWidthMin, '16px'));
  var MAX_WIDTH = stripUnit(remToPx(menuListWidthMax, '16px'));
  var MEDIUM_BREAKPOINT = "".concat(stripUnit(remToPx(sysSizeMediaMd, '16px')));
  /* istanbul ignore next */

  var isMobile = hasWindow() ? window.innerWidth < parseInt(MEDIUM_BREAKPOINT, 10) : false;

  var getRefCurrent = function (ref) {
    return (
      /* istanbul ignore next */
      ref && ref.current ? ref.current : null
    );
  };
  /**
   * To enable support for `shadow-dom` get the trigger element from `ref` or by `id`
   */


  var TRIGGER_ELEMENT = function getTriggerElement() {
    var triggerElement = getRefCurrent(triggerElementRef);
    /* istanbul ignore next */

    if (triggerElement) {
      return triggerElement;
    } // need to check existence of document object for SSR


    if (triggerElementId && hasDocument()) {
      return document.getElementById(triggerElementId);
    }

    return null;
  }();

  var calculateWidth = function () {
    var triggerWidth = TRIGGER_ELEMENT === null || TRIGGER_ELEMENT === void 0 ? void 0 : TRIGGER_ELEMENT.getBoundingClientRect().width;
    /* istanbul ignore next */

    if (isLoadingOptions) {
      var loader = getRefCurrent(loaderRef);
      loader.style.width = triggerWidth ? "".concat(triggerWidth, "px") : "".concat(MAX_WIDTH, "px");
    } else {
      var menu = getRefCurrent(menuRef);

      if (!menu) {
        return;
      }

      var menuWidth = menu.getBoundingClientRect().width;
      /* istanbul ignore next */

      if (triggerWidth > MIN_WIDTH && triggerWidth > menuWidth) {
        menu.style.width = "".concat(triggerWidth, "px");
      }
    }
  };
  /* Create a flat list of suggestions for highlight and selection by index */


  var normalizeSuggestions = function (suggestions) {
    return suggestions.reduce(function (acc, section) {
      /* istanbul ignore next */
      if (section === null || section === void 0 ? void 0 : section.suggestions) {
        acc.push.apply(acc, _toConsumableArray(section.suggestions));
      }

      return acc;
    }, []);
  };

  var setFocus = function (newIndex) {
    setFocusedIndex(newIndex);
  };

  var moveFocus = function (offset) {
    var tempOptions = hasSections ? normalizeSuggestions(options) : options;
    var nextIndex = Math.min(Math.max(focusedIndexRef.current + offset, 0), tempOptions.length - 1);
    setFocus(nextIndex);
  };

  var handleClickOutside = function (evt) {
    var wrapperCurrent = getRefCurrent(wrapperRef); // Trigger click is also handled here in React 17

    var isTriggerClicked = TRIGGER_ELEMENT === null || TRIGGER_ELEMENT === void 0 ? void 0 : TRIGGER_ELEMENT.contains(evt.target); // istanbul ignore next

    if ((!outsideClickCheck || outsideClickCheck(evt)) && !isTriggerClicked) {
      if (wrapperCurrent && !wrapperCurrent.contains(evt.target)) {
        onClose(evt, 'backdropClick');
      }
    }
  };

  var handleClick = function (index) {
    setFocus(index);
    onSelection(index);
  };
  /* istanbul ignore next */


  var getFirstPrefixPosition = function () {
    var list = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
    var typedText = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';
    return list.findIndex(function (option) {
      return option.primaryText.toLowerCase().startsWith(typedText.toLowerCase());
    });
  };
  /* eslint-disable complexity */


  var handleKey = function (evt) {
    evt.preventDefault();
    var focusedIndex = focusedIndexRef.current;
    /* istanbul ignore next */

    var KeyCode = new KeyboardCode(evt.key || evt.keyCode);

    if (KeyCode.IS_ARROW_UP) {
      setKeyboardActive(true);
      moveFocus(-1);
    } else if (KeyCode.IS_ARROW_DOWN) {
      setKeyboardActive(true);
      moveFocus(1);
    } else if (KeyCode.IS_ENTER) {
      onSelection(focusedIndex);
    } else if (KeyCode.IS_TAB || KeyCode.IS_ESC) {
      var closedBy = KeyCode.IS_TAB ? KeyboardCode.NAME_TAB : KeyboardCode.NAME_ESC; // We don't need to call onClose if we have a sheet -- it will do that for us!

      /* istanbul ignore next */

      if (typeof onClose === 'function' && !isMobile) {
        onClose(evt, closedBy);
      } // eslint-disable-next-line no-unused-expressions


      TRIGGER_ELEMENT === null || TRIGGER_ELEMENT === void 0 ? void 0 : TRIGGER_ELEMENT.focus();
    } else if (searchEnabled && evt.key.length === 1) {
      // Since we're handling keydown event, we might get values like
      // 'Shift' or 'Alt'. We don't want to search unless it's a single character
      clearTimeout(typeToFindTimer);
      searchText.current = searchText.current + evt.key; // Clear the typeToFindText if user doesn't consecutively type keys between 700 milliseconds
      //istanbul ignore next

      typeToFindTimer = setTimeout(function () {
        searchText.current = '';
      }, 700);
      var firstMatchedItemIndex = getFirstPrefixPosition(options, searchText.current);

      if (firstMatchedItemIndex >= 0) {
        setKeyboardActive(true);
        setFocus(firstMatchedItemIndex);
      }
    }
  };
  /* eslint-enable complexity */


  var handleKeyDown = function (evt) {
    handleKey(evt);
  };
  /* We need this method because we're trying to support index-based and
   * value-based operation at the same time. If we revert to only using
   * value-based, this code can get much simpler
   */


  var selectedValue = function getSelectedValue(currentValue, defaultValue) {
    if (currentValue !== undefined) {
      return currentValue;
    } // istanbul ignore next


    if (selectedIndex !== undefined && selectedIndex >= 0) {
      if (options && options[selectedIndex] && options[selectedIndex].value !== undefined) {
        return options[selectedIndex].value;
      }
    }

    return defaultValue;
  }(value);

  var renderImg = function (decoration) {
    return decoration && typeof decoration === 'string' ? jsx("img", {
      css: _styles.itemThumbnailImage,
      src: decoration,
      role: "none",
      "data-ppui": "true"
    }) : decoration;
  };

  var focusedIndex = ignoreKeyboard ? -1 : focusedIndexRef.current;

  var renderOptionList = function (menuOptions, section) {
    var normalizedIndex = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;

    if (!isOpen || !hasOptions) {
      return null;
    }

    return menuOptions.map(function (option, idx) {
      // We have to test against false because non-existence means it's selectable
      var index = hasSections ? idx + normalizedIndex : idx;
      var optionValue = option.hasOwnProperty('value') ? option.value : option.primaryText;
      var isSelected = !option.unselectable && (selectedValue === undefined ? selectedIndex === index : optionValue === selectedValue);
      var decoration = renderImg(option.decoration || option.imgSrc);
      return jsx(SelectionMenuOption, _extends({
        menuName: name
      }, option, {
        section: section,
        value: optionValue,
        isSelected: isSelected,
        isFocused: focusedIndex === index,
        isHilite: activeItem === index,
        className: itemClassName,
        decoration: decoration,
        key: "smenu_".concat(index),
        isMobile: isMobile,
        keyboardActive: keyboardActive,
        onItemClick: function onItemClick() {
          handleClick(index);
        },
        highlightText: highlightText,
        isTextHightlighted: isTextHightlighted,
        scrollToSelected: scrollToSelected,
        "data-ppui": "true"
      }));
    });
  };

  useLayoutEffect(function () {
    if (isOpen && !isMobile) {
      calculateWidth();
    }

    if (isOpen && hasOptions) {
      // Bind the event listener
      // @ts-ignore
      document.addEventListener('click', handleClickOutside);

      if (!ignoreKeyboard) {
        document.body.addEventListener('keydown', handleKeyDown);
      }

      if (!isMobile) {
        setKeyboardActive(initialItemFocus); // istanbul ignore next

        if (focusedIndex < 0) {
          setFocus(0);
        }
      } else {
        // set keyboard focus active to false, on mobile we don't show the focus indicator
        setKeyboardActive(false);
      }

      if (typeof onOpen === 'function') {
        onOpen(isMobile);
      }
    }

    return function () {
      // @ts-ignore
      document.removeEventListener('click', handleClickOutside);

      if (!ignoreKeyboard) {
        document.body.removeEventListener('keydown', handleKeyDown);
      }

      setKeyboardActive(false);
    };
  }, [isOpen, isLoadingOptions]);
  //pass dir manually to fix styles in sheet rtl
  //set to null in ltr so the attribute is not added

  /* istanbul ignore next */
  var direction = rtl ? 'rtl' : null;
  var optionList = hasSections ? function renderSections() {
    var currentNormalizedIndex = 0;
    return options.reduce(function (acc, option) {
      var _option$suggestions;

      // istanbul ignore next
      if (option === null || option === void 0 ? void 0 : (_option$suggestions = option.suggestions) === null || _option$suggestions === void 0 ? void 0 : _option$suggestions.length) {
        var uniqueKey = option.id ? option.id : Math.floor(Math.random() * (1000 - 1));
        acc.push(jsx("li", {
          role: "presentation",
          key: uniqueKey,
          "data-ppui": "true"
        }, option.title && jsx("div", {
          css: _styles.content,
          id: option.id,
          "aria-hidden": "true",
          "data-ppui": "true"
        }, jsx(CaptionText, {
          "data-ppui": "true"
        }, option.title)), jsx("ul", {
          role: "listbox",
          css: _styles.suggestionSublist,
          "aria-labelledby": option.id,
          "data-ppui": "true"
        }, renderOptionList(option.suggestions, option, currentNormalizedIndex))));
        currentNormalizedIndex += option.suggestions.length;
      }

      return acc;
    }, []);
  }() : renderOptionList(options);
  var menuListStyleOptions = {
    isMobile: isMobile,
    paddingTop: isMobile ? 0 : undefined
  };
  var contentList = optionList ? jsx("ul", _extends({
    css: _styles.menuList(menuListStyleOptions),
    className: className,
    id: id,
    tabIndex: 0,
    role: "listbox",
    "aria-hidden": !isOpen,
    ref: menuRef,
    dir: direction
  }, rest, {
    "data-ppui": "true"
  }), optionList) : null;
  var loadingSpinner = jsx("div", {
    ref: loaderRef,
    css: _styles.menuLoadingContainer,
    "data-ppui": "true"
  }, jsx(LoadingSpinner, {
    screenReaderText: "loading",
    size: "md",
    "data-ppui": "true"
  }));
  var content = isLoadingOptions ? loadingSpinner : contentList;
  return function bodyWrapper() {
    if (isMobile) {
      // istanbul ignore next
      var isFixedHeight = mobileHeaderContents ? '90' : undefined;
      return jsx(Sheet, {
        isOpen: isOpen,
        onCloseComplete: onClose,
        title: mobileTitle,
        headerClassName: mobileHeaderClassName,
        headerContents: mobileHeaderContents,
        noPadding: true,
        noCloseButton: false,
        mobileFixedHeight: mobileFixedHeight || isFixedHeight,
        className: sheetClassName,
        wrapperClassName: sheetWrapperClassName,
        returnFocusToTrigger: returnFocusToTrigger,
        "data-ppui": "true"
      }, content);
    }

    return isOpen && jsx("div", {
      ref: wrapperRef,
      css: _styles.menuContainer,
      className: containerClassName,
      "data-ppui-info": "selection-menu_3.14.6"
    }, content);
  }();
};

var _excluded$1 = ["className", "containerClassName", "itemClassName", "id", "initialItemFocus", "isOpen", "mobileTitle", "isLoadingOptions", "options", "onClose", "onSelection", "triggerRef", "mobileFixedHeight", "overrideMobileView"];

var getRefCurrent = function (ref) {
  return (
    /* istanbul ignore next */
    ref && ref.current ? ref.current : null
  );
};

var ActionMenu = function (_ref) {
  var className = _ref.className,
      containerClassName = _ref.containerClassName,
      itemClassName = _ref.itemClassName,
      _ref$id = _ref.id,
      id = _ref$id === void 0 ? uniqueId('actionMenu_') : _ref$id,
      _ref$initialItemFocus = _ref.initialItemFocus,
      initialItemFocus = _ref$initialItemFocus === void 0 ? true : _ref$initialItemFocus,
      isOpen = _ref.isOpen,
      mobileTitle = _ref.mobileTitle,
      _ref$isLoadingOptions = _ref.isLoadingOptions,
      isLoadingOptions = _ref$isLoadingOptions === void 0 ? false : _ref$isLoadingOptions,
      options = _ref.options,
      onClose = _ref.onClose,
      onSelection = _ref.onSelection,
      triggerRef = _ref.triggerRef,
      mobileFixedHeight = _ref.mobileFixedHeight,
      _ref$overrideMobileVi = _ref.overrideMobileView,
      overrideMobileView = _ref$overrideMobileVi === void 0 ? false : _ref$overrideMobileVi,
      rest = _objectWithoutProperties(_ref, _excluded$1);

  var tokens = useTheme();

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);

  var sysSizeMediaMd = tokens.sysSizeMediaMd;
  var MEDIUM_BREAKPOINT = "".concat(stripUnit(remToPx(sysSizeMediaMd, '16px')));
  var containerRef = useRef();
  var menuRef = useRef();
  var validateOptions = options && options.length;

  var _useState = useState(0),
      _useState2 = _slicedToArray(_useState, 2),
      selectedIndex = _useState2[0],
      setSelectedIndex = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      keyboardActive = _useState4[0],
      setKeyboardActive = _useState4[1];
  /* istanbul ignore next */


  var isMobile = !overrideMobileView && hasWindow() ? window.innerWidth < parseInt(MEDIUM_BREAKPOINT, 10) : false;

  var setFocus = function (offset) {
    var current = getRefCurrent(menuRef);
    /* istanbul ignore next */

    if (current) {
      var _listItems = current.querySelectorAll('[role="menuitem"]');

      if (_listItems.length) {
        var len = _listItems.length;
        var nextIndex = selectedIndex + offset;
        var curIndex = nextIndex >= 0 && nextIndex <= len - 1 ? nextIndex : 0;
        setSelectedIndex(curIndex);
      }
    }
  };

  var handleClickOutside = function (evt) {
    var wrapperCurrent = getRefCurrent(containerRef); // triggerRef is required for React 17

    if (wrapperCurrent && !wrapperCurrent.contains(evt.target) && triggerRef && !triggerRef.current.contains(evt.target)) {
      onClose(evt);
    } else if (!triggerRef && wrapperCurrent && !wrapperCurrent.contains(evt.target)) {
      onClose(evt);
    }
  };
  /* eslint-disable complexity */


  /* eslint-enable complexity */
  useEffect(function () {
    /* istanbul ignore next */
    if (isOpen) {
      if (!isMobile) {
        setFocus(0);
        setKeyboardActive(initialItemFocus);
      }

      document.addEventListener('click', handleClickOutside);
      window.addEventListener('resize', onClose);
    } else {
      document.removeEventListener('click', handleClickOutside);
      window.removeEventListener('resize', onClose);
    }

    return function () {
      setSelectedIndex(0);
      setKeyboardActive(false);
      document.removeEventListener('click', handleClickOutside);
      window.removeEventListener('resize', onClose);
    };
  }, [isOpen, options]);
  var optionList = validateOptions ? jsx(List, _extends({
    options: options.map(function (_ref2, optionIndex) {
      var itemLeft = _ref2.itemLeft,
          title = _ref2.title,
          navigationOptions = _ref2.navigationOptions;
      return {
        decoration: itemLeft ? {
          thumbnail: itemLeft
        } : null,
        label: title,
        className: itemClassName,
        role: 'menuitem',
        onClick: !navigationOptions ? function (event) {
          return onSelection(optionIndex, event);
        } : null,
        navigationOptions: navigationOptions
      };
    }),
    focusActive: keyboardActive,
    focusedIndex: selectedIndex,
    listStyles: _styles.menuList({
      isMobile: isMobile
    }),
    className: className,
    id: id,
    role: "menu",
    "aria-hidden": !isOpen,
    ref: menuRef,
    onKeyDown: function handleKey(evt) {
      evt.preventDefault();
      /* istanbul ignore next */

      var KeyCode = new KeyboardCode(evt.key || evt.keyCode);
      var triggerEl = getRefCurrent(triggerRef);

      if (KeyCode.IS_ARROW_UP) {
        setKeyboardActive(true);
        setFocus(-1);
      } else if (KeyCode.IS_ARROW_DOWN) {
        setKeyboardActive(true);
        setFocus(1);
      } else if (KeyCode.IS_ENTER) {
        onSelection(selectedIndex, evt);
        var anchor = evt.target;
        /* istanbul ignore next */

        if (anchor.href) {
          anchor.click();
        }
      } else if (KeyCode.IS_TAB || KeyCode.IS_ESC) {
        onClose(evt);
      }

      if ((KeyCode.IS_TAB || KeyCode.IS_ESC || KeyCode.IS_ENTER) && triggerEl) {
        triggerEl.focus();
      }
    }
  }, rest, {
    "data-ppui": "true"
  })) : null;
  var listItems = isLoadingOptions ? jsx("div", {
    css: _styles.menuLoadingContainer,
    "data-ppui": "true"
  }, jsx(LoadingSpinner, {
    screenReaderText: "loading",
    size: "md",
    "data-ppui": "true"
  })) : optionList;
  var container = isMobile ? jsx(Sheet, {
    title: mobileTitle,
    isOpen: isOpen,
    onCloseComplete: onClose,
    noPadding: true,
    mobileFixedHeight: mobileFixedHeight,
    "data-ppui": "true"
  }, listItems) : jsx("nav", {
    css: _styles.menuContainer,
    className: containerClassName,
    ref: containerRef,
    "data-ppui-info": "action-menu_3.14.6"
  }, listItems);
  return isOpen && container;
};

export { ActionMenu, SelectionMenu, styles as menuStyles };
